import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';


/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  /** @type {string | null} */
  #lastHoveredMediaId = null;

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Setup robust swatch hover detection using pointermove
    this.#setupSwatchHoverFix();

    // Auto-select variant for split variant cards
    this.#autoSelectVariantForSplitCard();

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  /**
   * Called by the morph library after the component's DOM is updated.
   * This is critical for handling sort/filter changes that morph the DOM.
   */
  updatedCallback() {
    // After morph, the DOM may have changed but component state persists.
    // Reset split card state flags so the card can be properly re-initialized.
    if (this.#splitCardVariantId) {
      // Reset flags that control one-time initialization
      this.#splitCardRevealed = false;
      this.#splitCardSwatchSelected = false;
      this.#splitCardSlideshowUpdated = false;

      // Re-run split card initialization
      this.#autoSelectVariantForSplitCard();
    }
  }

  /**
   * Sets up robust swatch hover detection using pointermove.
   * This fixes an issue where pointerenter events don't fire reliably
   * when moving between adjacent swatches after clicking.
   */
  #setupSwatchHoverFix() {
    // Support both standard swatch row and LWS color swatches
    const swatchContainers = this.querySelectorAll('.product-card__swatch-row, .lws-product-color-swatches');
    if (swatchContainers.length === 0) return;

    swatchContainers.forEach((container) => {
      container.addEventListener('pointermove', this.#handleSwatchPointermove);
      container.addEventListener('pointerleave', this.#handleSwatchPointerleave);
    });

    // Also set up click/change listeners for all swatch inputs (fallback in case variantUpdate doesn't fire)
    const allSwatchInputs = this.querySelectorAll('.lws-color-swatch-input, .variant-option__button-label input[type="radio"]');
    allSwatchInputs.forEach((input) => {
      input.addEventListener('change', this.#handleSwatchInputChange);
    });
  }

  /**
   * Handles change events on swatch inputs (both LWS and standard).
   * @param {Event} event - The change event.
   */
  #handleSwatchInputChange = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!input.checked) return;

    // Get availability and sale status
    const variantAvailable = input.dataset.variantAvailable === 'true' ||
      (input.dataset.availableCount && parseInt(input.dataset.availableCount, 10) > 0);
    const variantOnSale = input.dataset.variantOnSale === 'true';

    // Update for split variant cards
    if (this.#splitCardVariantId) {
      // Update title
      if (input.dataset.variantTitle) {
        this.#updateSplitCardTitle(input.dataset.variantTitle);
      }

      // Update price and availability
      if (input.dataset.variantPrice) {
        this.#splitCardVariantPrice = input.dataset.variantPrice;
        this.#splitCardVariantCompareAtPrice = input.dataset.variantCompareAtPrice || '';
        this.#splitCardVariantOnSale = variantOnSale;
        this.#splitCardVariantAvailable = variantAvailable;

        this.#updateSplitCardPrice();
        this.#updateSplitCardBadge();
      } else {
        // Even without price data, update availability and badge
        this.#splitCardVariantAvailable = variantAvailable;
        this.#splitCardVariantOnSale = variantOnSale;
        this.#updateSplitCardBadge();
      }

      // Update the slideshow to show the variant's image
      const mediaId = input.dataset.optionMediaId;
      if (mediaId && this.refs.slideshow?.select) {
        this.refs.slideshow.select({ id: mediaId }, undefined, { animate: false });
      }
    } else {
      // For regular product cards, just update the badge
      this.#updateSplitCardBadge(variantAvailable, variantOnSale);
    }
  };

  /**
   * Auto-selects the variant for split variant cards.
   * When a product card is inside a container with data-variant-id,
   * this will pre-select the corresponding swatch.
   */
  #autoSelectVariantForSplitCard() {
    // Check if this card has data-is-variant-card attribute directly
    const isVariantCard = this.hasAttribute('data-is-variant-card');
    const directVariantId = this.dataset.variantId;

    // Check if this card is inside a split variant card container
    // Walk up the DOM tree looking for the data attribute
    let parent = this.parentElement;
    let variantId = directVariantId || null;
    let variantTitle = null;
    let productTitle = null;

    // If we have direct attributes, use them
    let variantMediaId = null;
    if (isVariantCard && directVariantId) {
      variantId = directVariantId;
    } else {
      // Search up to 10 levels for the variant container
      for (let i = 0; i < 10 && parent; i++) {
        if (parent.dataset?.variantId && parent.dataset?.isVariantCard) {
          variantId = parent.dataset.variantId;
          variantTitle = parent.dataset.variantTitle;
          productTitle = parent.dataset.productTitle;
          variantMediaId = parent.dataset.variantMediaId || null;
          this.#splitCardProductType = parent.dataset.productType || '';
          this.#splitCardVariantPrice = parent.dataset.variantPrice || '';
          this.#splitCardVariantCompareAtPrice = parent.dataset.variantCompareAtPrice || '';
          this.#splitCardVariantOnSale = parent.dataset.variantOnSale === 'true';
          // Capture availability from parent data attribute
          this.#splitCardVariantAvailable = parent.dataset.variantSoldOut !== 'true' &&
            parent.dataset.variantAvailable !== 'false';

          // Capture color schemes from existing badge if present
          const existingBadge = parent.querySelector('.product-badges__badge');
          if (existingBadge) {
            const colorClass = Array.from(existingBadge.classList).find(c => c.startsWith('color-'));
            const badgeText = existingBadge.textContent?.toLowerCase().trim() || '';
            if (badgeText.includes('sold') || badgeText.includes('out')) {
              this.#soldOutColorScheme = colorClass || 'color-scheme-4';
            } else if (badgeText.includes('sale')) {
              this.#saleColorScheme = colorClass || 'color-scheme-3';
            }
          }
          break;
        }
        parent = parent.parentElement;
      }
    }

    // Store the variant media ID for slideshow update
    this.#splitCardVariantMediaId = variantMediaId;

    if (!variantId) {
      return;
    }

    // Store the variant ID and titles for this split card
    this.#splitCardVariantId = variantId;
    this.#splitCardVariantTitle = variantTitle;
    this.#splitCardProductTitle = productTitle;

    // Track load start time for this card
    this.#splitCardLoadStart = performance.now();

    // First, select the swatch immediately to show correct visual state
    this.#selectSplitCardSwatch();

    // Update the product card link URL to include the variant ID
    this.#updateSplitCardUrl();

    // Update the product title to include variant name
    this.#updateSplitCardTitle();

    // Update the price to show this variant's price
    this.#updateSplitCardPrice();

    // Update the SALE badge visibility based on variant's sale status
    this.#updateSplitCardBadge();

    // Also retry price and badge updates with delays to handle DOM timing issues
    // The price component and badges may not be fully rendered yet
    setTimeout(() => {
      this.#updateSplitCardPrice();
      this.#updateSplitCardBadge();
    }, 50);
    setTimeout(() => {
      this.#updateSplitCardPrice();
      this.#updateSplitCardBadge();
    }, 150);
    setTimeout(() => {
      this.#updateSplitCardPrice();
      this.#updateSplitCardBadge();
    }, 300);

    // Listen for the slideshow's initial select event, then override with our selection
    // The slideshow dispatches SlideshowSelectEvent after it finishes initializing
    this.addEventListener(
      SlideshowSelectEvent.eventName,
      () => {
        // Run on next frame to ensure slideshow has finished its initialization
        requestAnimationFrame(() => this.#updateSplitCardSlideshow());
      },
      { once: true }
    );

    // Also try with timeouts as fallback in case the event doesn't fire
    setTimeout(() => this.#updateSplitCardSlideshow(), 100);
    setTimeout(() => this.#updateSplitCardSlideshow(), 300);

    // Pre-fetch the product page for quick-add button so it's ready on page load
    // This ensures the quick-add button is visible without requiring hover
    setTimeout(() => {
      this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
    }, 100);
  }

  /** @type {string | null} */
  #splitCardVariantId = null;

  /** @type {string | null} */
  #splitCardVariantMediaId = null;

  /** @type {string | null} */
  #splitCardVariantTitle = null;

  /** @type {string | null} */
  #splitCardProductTitle = null;

  /** @type {string} */
  #splitCardProductType = '';

  /** @type {string} */
  #splitCardVariantPrice = '';

  /** @type {string} */
  #splitCardVariantCompareAtPrice = '';

  /** @type {boolean} */
  #splitCardVariantOnSale = false;

  /** @type {boolean} */
  #splitCardVariantAvailable = true;

  /** @type {boolean} */
  #splitCardSwatchSelected = false;

  /** @type {boolean} */
  #splitCardSlideshowUpdated = false;

  /** @type {boolean} */
  #splitCardRevealed = false;

  /** @type {number | null} */
  #splitCardLoadStart = null;

  /**
   * Formats a price in cents to a currency string.
   * @param {string|number} priceInCents - The price in cents
   * @returns {string} The formatted price string
   */
  #formatPrice(priceInCents) {
    const cents = parseInt(priceInCents, 10);
    if (isNaN(cents)) return '';
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  /**
   * Updates the price display for split variant cards to show the specific variant's price.
   */
  #updateSplitCardPrice() {
    const price = this.#splitCardVariantPrice;
    const compareAtPrice = this.#splitCardVariantCompareAtPrice;

    if (!price) {
      return;
    }

    // Find ALL price components (there can be multiple - normal view and zoom-out view)
    const priceComponents = this.querySelectorAll('product-price, price-component, [data-block-type="price"]');
    if (priceComponents.length === 0) {
      return;
    }

    // Determine if this variant is on sale
    const isOnSale = compareAtPrice && compareAtPrice !== '' && compareAtPrice !== 'null' &&
      parseInt(compareAtPrice, 10) > parseInt(price, 10);

    // Update ALL price components
    priceComponents.forEach((priceComponent, index) => {
      let priceContainer = priceComponent.querySelector('[ref="priceContainer"]');
      // Fallback: look for any container with price elements
      if (!priceContainer) {
        priceContainer = priceComponent.querySelector('.price')?.parentElement;
      }
      if (!priceContainer) {
        return;
      }

      // Find or create price elements
      let priceElement = priceContainer.querySelector('.price');
      let compareAtElement = priceContainer.querySelector('.compare-at-price, s.compare-at-price');

      // Update the main price
      if (priceElement) {
        priceElement.textContent = this.#formatPrice(price);
      }

      // Handle compare-at price
      if (isOnSale) {
        // Show compare-at price
        if (!compareAtElement) {
          compareAtElement = document.createElement('s');
          compareAtElement.className = 'compare-at-price';
          // Insert after the price element to maintain correct visual order with row-reverse
          if (priceElement) {
            priceElement.after(compareAtElement);
          } else {
            priceContainer.appendChild(compareAtElement);
          }
        }
        compareAtElement.textContent = this.#formatPrice(compareAtPrice);
        // Use setProperty with 'important' to override CSS !important rules
        compareAtElement.style.setProperty('display', 'inline-flex', 'important');
      } else if (compareAtElement) {
        // Hide compare-at price if no sale - use 'important' to override CSS !important rules
        compareAtElement.style.setProperty('display', 'none', 'important');
      }
    });
  }

  /** @type {string | null} */
  #soldOutColorScheme = null;

  /** @type {string | null} */
  #saleColorScheme = null;

  /**
   * Updates the badge visibility for product cards based on variant availability and sale status.
   * Shows SOLD OUT badge if variant is unavailable, SALE badge if on sale, or hides badge otherwise.
   * @param {boolean} [isAvailable] - Override for variant availability (for hover preview)
   * @param {boolean} [isOnSale] - Override for variant sale status (for hover preview)
   */
  #updateSplitCardBadge(isAvailable, isOnSale) {
    // Use provided values or fall back to stored state
    const variantAvailable = isAvailable !== undefined ? isAvailable : this.#splitCardVariantAvailable;
    const variantOnSale = isOnSale !== undefined ? isOnSale : this.#splitCardVariantOnSale;

    // Check if this is a split variant card (badge rendered outside product-card as sibling)
    const parent = this.closest('.variant-card-wrapper, [data-is-variant-card]');
    const isSplitVariantCard = parent && (this.#splitCardVariantId || parent.hasAttribute('data-variant-id'));

    let badgeContainer = /** @type {HTMLElement | null} */ (null);

    if (isSplitVariantCard) {
      // For split variant cards, look for the badge in the parent wrapper FIRST
      // The badge is rendered as a sibling to the product-card, not inside it
      badgeContainer = /** @type {HTMLElement | null} */ (parent.querySelector('.variant-card-badge'));

      // If no badge container exists, create one
      if (!badgeContainer) {
        badgeContainer = document.createElement('div');
        badgeContainer.className = 'variant-card-badge product-badges product-badges--bottom-left';
        badgeContainer.style.cssText = '--badge-border-radius: 3px; --badge-font-family: var(--font-body--family); --badge-font-weight: var(--font-body--weight); --badge-text-transform: uppercase;';
        parent.appendChild(badgeContainer);
      }
    } else {
      // For regular product cards, look inside the product-card element
      badgeContainer = /** @type {HTMLElement | null} */ (this.querySelector('.product-badges'));
      if (!badgeContainer) return;
    }

    let badge = /** @type {HTMLElement | null} */ (badgeContainer.querySelector('.product-badges__badge'));

    // Store color scheme classes from existing badge for reuse
    if (badge && !this.#soldOutColorScheme && !this.#saleColorScheme) {
      const existingColorClass = Array.from(badge.classList).find(c => c.startsWith('color-'));
      // Try to infer which type of badge this is based on content
      const badgeText = badge.textContent?.toLowerCase().trim() || '';
      if (badgeText.includes('sold') || badgeText.includes('out')) {
        this.#soldOutColorScheme = existingColorClass || 'color-scheme-4';
      } else if (badgeText.includes('sale')) {
        this.#saleColorScheme = existingColorClass || 'color-scheme-3';
      }
    }

    // If we still don't have color schemes, try to find them from sibling cards in the grid
    if (!this.#soldOutColorScheme || !this.#saleColorScheme) {
      const grid = this.closest('.product-grid, results-list');
      if (grid) {
        // Look for badges both inside product cards and in variant-card-badge containers
        const allBadges = grid.querySelectorAll('.variant-card-badge .product-badges__badge, .product-badges .product-badges__badge');
        for (const siblingBadge of allBadges) {
          const colorClass = Array.from(siblingBadge.classList).find(c => c.startsWith('color-'));
          if (!colorClass) continue;
          const text = siblingBadge.textContent?.toLowerCase().trim() || '';
          if (!this.#soldOutColorScheme && (text.includes('sold') || text.includes('out'))) {
            this.#soldOutColorScheme = colorClass;
          } else if (!this.#saleColorScheme && text.includes('sale')) {
            this.#saleColorScheme = colorClass;
          }
          if (this.#soldOutColorScheme && this.#saleColorScheme) break;
        }
      }
    }

    // Determine what badge state should be shown
    if (!variantAvailable) {
      // Variant is sold out - show SOLD OUT badge
      if (!badge) {
        // Create the badge if it doesn't exist
        badge = document.createElement('div');
        badgeContainer.appendChild(badge);
      }
      const colorScheme = this.#soldOutColorScheme || 'color-scheme-4';
      badge.className = `product-badges__badge product-badges__badge--rectangle ${colorScheme}`;
      badge.textContent = 'Sold out';
      badgeContainer.style.display = '';
      badge.style.display = '';
    } else if (variantOnSale) {
      // Variant is on sale - show SALE badge
      if (!badge) {
        badge = document.createElement('div');
        badgeContainer.appendChild(badge);
      }
      const colorScheme = this.#saleColorScheme || 'color-scheme-3';
      badge.className = `product-badges__badge product-badges__badge--rectangle ${colorScheme}`;
      badge.textContent = 'Sale';
      badgeContainer.style.display = '';
      badge.style.display = '';
    } else {
      // No badge needed - hide it
      badgeContainer.style.display = 'none';
    }
  }

  /**
   * Escapes special regex characters in a string.
   * @param {string} str - The string to escape
   * @returns {string} The escaped string
   */
  #escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Computes the display title for split variant cards.
   * Logic:
   * 1. Remove product type (and plural) from product title to get prefix
   * 2. Remove prefix from variant title if it exists
   * 3. Combine: prefix + cleaned variant title
   * @param {string} variantTitle - The variant title
   * @returns {string} The computed display title
   */
  #computeSplitCardDisplayTitle(variantTitle) {
    const productTitle = this.#splitCardProductTitle || '';
    const productType = this.#splitCardProductType || '';

    if (!productType || !productTitle) {
      return variantTitle;
    }

    // Remove product type (and common plural forms) from product title to get prefix
    // Handle: "band" -> removes "band", "bands"
    // Handle: "adapter" -> removes "adapter", "adapters"
    const escapedType = this.#escapeRegex(productType);
    const typePattern = new RegExp(`\\s*${escapedType}s?\\s*$`, 'i');
    const prefix = productTitle.replace(typePattern, '').trim();

    if (!prefix) {
      return variantTitle;
    }

    // Remove prefix from variant title if it exists at the start
    const escapedPrefix = this.#escapeRegex(prefix);
    const prefixPattern = new RegExp(`^${escapedPrefix}\\s*`, 'i');
    const cleanedVariantTitle = variantTitle.replace(prefixPattern, '').trim();

    // Combine prefix + cleaned variant title
    return `${prefix} ${cleanedVariantTitle}`;
  }

  /**
   * Updates the product title to show the variant name for split cards.
   * Also updates the subtitle with the product name.
   * @param {string} [variantTitle] - Optional variant title to display
   */
  #updateSplitCardTitle(variantTitle) {
    const rawTitle = variantTitle || this.#splitCardVariantTitle;
    const productName = this.#splitCardProductTitle;

    if (!rawTitle) {
      return;
    }

    // Compute the display title using the prefix logic
    const displayTitle = this.#computeSplitCardDisplayTitle(rawTitle);

    // Find the product title element - it's inside a text-block with a <p> tag
    const titleLink = this.refs.productTitleLink || this.querySelector('[ref="productTitleLink"]');
    const titleElement = titleLink?.querySelector('p') || this.querySelector('.text-block p');

    if (titleElement) {
      // Update the title to show the computed display title (with trademark formatting)
      titleElement.innerHTML = this.#formatBrandText(displayTitle);

      // Update the subtitle with the product name (with trademark formatting)
      // The subtitle element is rendered from Liquid (product-title.liquid)
      if (productName) {
        const subtitle = this.querySelector('.split-card-subtitle');
        if (subtitle) {
          // Check if this is a skeleton placeholder or already has real content
          const isSkeleton = subtitle.hasAttribute('data-skeleton');
          const currentContent = subtitle.textContent?.trim();
          const hasRealContent = currentContent && currentContent !== '' && currentContent !== '\u00A0' && !isSkeleton;

          // Only update if it's a skeleton placeholder or doesn't have real content yet
          if (isSkeleton || !hasRealContent) {
            // Use universal brand-tm class for subtitle (not product-card-tm)
            subtitle.innerHTML = this.#formatBrandText(productName, 'brand-tm');
            subtitle.removeAttribute('data-skeleton');
          }
        }
      }
    }
  }

  /**
   * Formats brand names with trademark symbols.
   * @param {string} text - The text to format
   * @param {string} [tmClass='product-card-tm'] - CSS class for the trademark element
   * @returns {string} The formatted HTML string
   */
  #formatBrandText(text, tmClass = 'product-card-tm') {
    if (!text) return '';

    let formatted = text;

    // MagicSwap™ - trademark (handle both with and without existing symbol)
    // First handle text that already has ™ symbol
    formatted = formatted.replace(/MagicSwap™/gi, `MagicSwap<sup class="${tmClass}">™</sup>`);
    // Then handle text without ™ (negative lookahead to avoid double-replacing)
    formatted = formatted.replace(/MagicSwap(?!™)(?!<sup)/gi, `MagicSwap<sup class="${tmClass}">™</sup>`);

    return formatted;
  }

  /**
   * Selects the correct swatch for split variant cards.
   */
  #selectSplitCardSwatch() {
    const variantId = this.#splitCardVariantId;
    if (!variantId || this.#splitCardSwatchSelected) return;

    // Find the swatch input with the matching variant ID
    // Try direct input selector first, then look for input inside a wrapper with the variant ID
    let swatchInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector(`input[data-variant-id="${variantId}"]`)
    );

    // Fallback: look for input inside a label/wrapper with data-variant-id
    if (!swatchInput) {
      const wrapper = this.querySelector(`[data-variant-id="${variantId}"]`);
      swatchInput = /** @type {HTMLInputElement | null} */ (wrapper?.querySelector('input[type="radio"]'));
    }

    if (!swatchInput) return;

    // Mark as selected and check the input
    this.#splitCardSwatchSelected = true;
    swatchInput.checked = true;

    // Trigger change event to update the variant picker's visual state
    swatchInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Updates all product card links with the correct variant ID for split variant cards.
   */
  #updateSplitCardUrl() {
    const variantId = this.#splitCardVariantId;
    if (!variantId) return;

    const { productCardLink, cardGalleryLink, productTitleLink } = this.refs;

    // Update all links with the variant parameter
    const updateLink = (/** @type {HTMLAnchorElement | null | undefined} */ link) => {
      if (!(link instanceof HTMLAnchorElement) || !link.href) return;
      try {
        const url = new URL(link.href);
        url.searchParams.set('variant', variantId);
        link.href = url.toString();
      } catch (e) {
        // Invalid URL, skip
      }
    };

    updateLink(productCardLink);
    updateLink(cardGalleryLink);
    updateLink(productTitleLink);
  }

  /**
   * Reveals this split card (removes skeleton, shows content)
   */
  #revealSplitCard() {
    if (this.#splitCardRevealed) return;
    this.#splitCardRevealed = true;

    this.setAttribute('data-variant-loaded', 'true');

    // Preload other variant images in background for smooth swatch hover
    this.#preloadOtherVariantImages();
  }

  /**
   * Preloads other variant images after reveal so swatch hover doesn't flicker
   */
  #preloadOtherVariantImages() {
    // Small delay to not compete with other page resources
    setTimeout(() => {
      const lazyImages = this.querySelectorAll('.card-gallery img[loading="lazy"]');
      lazyImages.forEach((img) => {
        // Remove lazy loading to trigger immediate load
        img.removeAttribute('loading');
      });
    }, 100);
  }

  /**
   * Updates the slideshow to show the correct image for split variant cards.
   * Selects the variant's image slide, waits for it to load, then reveals.
   * Other images are lazy loaded for swatch hover preview.
   */
  #updateSplitCardSlideshow() {
    const variantId = this.#splitCardVariantId;

    if (!variantId || this.#splitCardSlideshowUpdated) return;

    // Mark as updated so we don't repeat
    this.#splitCardSlideshowUpdated = true;

    const { slideshow } = this.refs;

    // Select the correct slide for this variant using the swatch's optionMediaId
    // This is the same logic used in #resetVariant which correctly shows the variant image
    const selectedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector(`input[data-variant-id="${variantId}"]`)
    );

    if (selectedInput && slideshow?.select) {
      const mediaId = selectedInput.dataset.optionMediaId;
      if (mediaId) {
        // Select the variant's image slide
        slideshow.select({ id: mediaId }, undefined, { animate: false });
      }
    }

    // Find the currently displayed image
    const img = this.querySelector('.card-gallery img');
    if (!img) {
      // No image - reveal immediately
      this.#revealSplitCard();
      return;
    }

    // Check if already loaded
    if (img.complete && img.naturalHeight !== 0) {
      this.#revealSplitCard();
      return;
    }

    // Wait for image to load
    img.addEventListener('load', () => this.#revealSplitCard(), { once: true });
    img.addEventListener('error', () => this.#revealSplitCard(), { once: true });

    // Fallback timeout per card (2 seconds) in case image never loads
    setTimeout(() => {
      if (!this.#splitCardRevealed) {
        this.#revealSplitCard();
      }
    }, 2000);
  }

  /**
   * Handles pointermove on the swatch row to detect swatch changes.
   * @param {PointerEvent} event - The pointer event.
   */
  #handleSwatchPointermove = (event) => {
    if (event.pointerType !== 'mouse') return;

    const target = /** @type {Element} */ (event.target);
    // Support both standard swatches and LWS color swatches
    const label = target.closest('.variant-option__button-label, .lws-color-swatch-wrapper');

    if (label) {
      const mediaId = label.getAttribute('data-media-id');
      // Get variant ID from label or from input inside
      let variantId = label.getAttribute('data-variant-id');
      const input = /** @type {HTMLInputElement | null} */ (label.querySelector('input[data-variant-id]'));
      if (!variantId && input) {
        variantId = input.dataset.variantId || null;
      }

      if ((mediaId || variantId) && (mediaId !== this.#lastHoveredMediaId || variantId !== this.#lastHoveredVariantId)) {
        this.#lastHoveredMediaId = mediaId;
        this.#lastHoveredVariantId = variantId;

        if (mediaId) {
          this.previewVariant(mediaId);
        }

        // Preview title, price, and badge
        if (input) {
          if (this.#splitCardVariantId) {
            // For split variant cards, update title, price, and badge
            this.#previewVariantInfo(input);
          } else {
            // For regular product cards, just update the badge
            const variantAvailable = input.dataset.variantAvailable === 'true' ||
              (input.dataset.availableCount && parseInt(input.dataset.availableCount, 10) > 0);
            const variantOnSale = input.dataset.variantOnSale === 'true';
            this.#updateSplitCardBadge(variantAvailable, variantOnSale);
          }
        }
      }
    }
  };

  /**
   * Resets the last hovered media ID when pointer leaves the swatch row.
   */
  #handleSwatchPointerleave = () => {
    this.#lastHoveredMediaId = null;
    this.#lastHoveredVariantId = null;

    // Restore original title, price, and badge
    if (this.#splitCardVariantId) {
      this.#restoreSelectedVariantInfo();
    } else {
      // For regular product cards, restore the badge based on selected swatch
      const selectedInput = /** @type {HTMLInputElement | null} */ (
        this.querySelector('input[type="radio"]:checked[data-variant-id]')
      );
      if (selectedInput) {
        const variantAvailable = selectedInput.dataset.variantAvailable === 'true' ||
          (selectedInput.dataset.availableCount && parseInt(selectedInput.dataset.availableCount, 10) > 0);
        const variantOnSale = selectedInput.dataset.variantOnSale === 'true';
        this.#updateSplitCardBadge(variantAvailable, variantOnSale);
      }
    }
  };

  /** @type {string | null} */
  #lastHoveredVariantId = null;

  /**
   * Temporarily previews a variant's title and price on hover.
   * @param {HTMLInputElement} input - The swatch input element with variant data.
   */
  #previewVariantInfo(input) {
    const variantTitle = input.dataset.variantTitle;
    const variantPrice = input.dataset.variantPrice;
    const variantCompareAtPrice = input.dataset.variantCompareAtPrice || '';
    const variantOnSale = input.dataset.variantOnSale === 'true';
    // Check availability from data-variant-available or data-available-count
    const variantAvailable = input.dataset.variantAvailable === 'true' ||
      (input.dataset.availableCount && parseInt(input.dataset.availableCount, 10) > 0);

    // Update title temporarily
    if (variantTitle) {
      this.#updateSplitCardTitle(variantTitle);
    }

    // Update price temporarily
    if (variantPrice) {
      const originalPrice = this.#splitCardVariantPrice;
      const originalCompareAt = this.#splitCardVariantCompareAtPrice;
      const originalOnSale = this.#splitCardVariantOnSale;
      const originalAvailable = this.#splitCardVariantAvailable;

      this.#splitCardVariantPrice = variantPrice;
      this.#splitCardVariantCompareAtPrice = variantCompareAtPrice;
      this.#splitCardVariantOnSale = variantOnSale;
      this.#splitCardVariantAvailable = variantAvailable;

      this.#updateSplitCardPrice();
      this.#updateSplitCardBadge();

      // Restore original values (they'll be used when reverting)
      this.#splitCardVariantPrice = originalPrice;
      this.#splitCardVariantCompareAtPrice = originalCompareAt;
      this.#splitCardVariantOnSale = originalOnSale;
      this.#splitCardVariantAvailable = originalAvailable;
    } else {
      // Even without price data, update badge based on availability
      this.#updateSplitCardBadge(variantAvailable, variantOnSale);
    }
  }

  /**
   * Restores the selected variant's title and price after hover ends.
   */
  #restoreSelectedVariantInfo() {
    // Find the currently selected/checked swatch
    const selectedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    if (selectedInput) {
      // Update title to selected variant
      if (selectedInput.dataset.variantTitle) {
        this.#updateSplitCardTitle(selectedInput.dataset.variantTitle);
      }

      // Update price and availability to selected variant
      if (selectedInput.dataset.variantPrice) {
        this.#splitCardVariantPrice = selectedInput.dataset.variantPrice;
        this.#splitCardVariantCompareAtPrice = selectedInput.dataset.variantCompareAtPrice || '';
        this.#splitCardVariantOnSale = selectedInput.dataset.variantOnSale === 'true';
      }

      // Update availability from data attributes
      this.#splitCardVariantAvailable = selectedInput.dataset.variantAvailable === 'true' ||
        (selectedInput.dataset.availableCount && parseInt(selectedInput.dataset.availableCount, 10) > 0);

      this.#updateSplitCardPrice();
      this.#updateSplitCardBadge();
    } else {
      // Fall back to original split card data
      this.#updateSplitCardTitle();
      this.#updateSplitCardPrice();
      this.#updateSplitCardBadge();
    }
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    // Cancel any pending resetVariant to prevent race conditions
    this.resetVariant.cancel();
    // Reset hover tracking so it doesn't interfere with the new selection
    this.#lastHoveredMediaId = null;

    // For split variant cards, skip the price morph - we handle price updates ourselves
    // This prevents the morph from overwriting our custom price structure with dividers
    if (!this.#splitCardVariantId) {
      this.updatePrice(event);
    }
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Update the title, price, and badge based on selected variant
    const selectedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );
    if (selectedInput) {
      // Get availability and sale status
      const variantAvailable = selectedInput.dataset.variantAvailable === 'true' ||
        (selectedInput.dataset.availableCount && parseInt(selectedInput.dataset.availableCount, 10) > 0);
      const variantOnSale = selectedInput.dataset.variantOnSale === 'true';

      if (this.#splitCardVariantId) {
        // For split variant cards, also update title and price
        if (selectedInput.dataset.variantTitle) {
          this.#updateSplitCardTitle(selectedInput.dataset.variantTitle);
        }
        if (selectedInput.dataset.variantPrice) {
          this.#splitCardVariantPrice = selectedInput.dataset.variantPrice;
          this.#splitCardVariantCompareAtPrice = selectedInput.dataset.variantCompareAtPrice || '';
          this.#updateSplitCardPrice();
        }
        this.#splitCardVariantAvailable = variantAvailable;
        this.#splitCardVariantOnSale = variantOnSale;
        this.#updateSplitCardBadge();
      } else {
        // For regular product cards, just update the badge
        this.#updateSplitCardBadge(variantAvailable, variantOnSale);
      }
    }

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    const slide = event.detail?.slide;
    if (slide) {
      slide.removeAttribute('hidden');
      slide.setAttribute('aria-hidden', 'false');
    }

    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    const slide = slideshow.refs.slides?.find((item) => item.getAttribute('slide-id') == id);
    if (!slide) return;

    // Let slideshow.select handle revealing hidden slides - don't modify attributes here
    // as it corrupts the slideshow's current slide tracking
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
